import { useState, useRef, useCallback } from 'react';
import { sugerirTextosIA, feedbackIA } from './productApi';

// IA-1 (remate): UNA generación compartida entre los campos (descripción corta/larga, título SEO,
// meta), pero revelado y carga POR CAMPO:
//  - Al pedir en un campo, solo ESE campo queda «generando» y solo bajo ÉL se revela la tarjeta.
//  - El resultado se cachea: pedir en otro campo lo muestra al instante, SIN nueva llamada.
//  - Si ya hay una llamada en vuelo y pides otro campo, ESPERA la misma (nunca dos a la vez).
//  - `regenerar(campo, base)` pide una fresca y la APILA en el historial (las anteriores se conservan).
//  - HISTORIAL (remate 2): `irAnterior`/`irSiguiente` mueven entre generaciones (contador indice/total);
//    `sug` es la generación visible, así «Usar» aplica la que se está viendo.
//  - `base` (remate 3): 'texto' (partir de lo escrito) | 'cero' (escribir desde cero) → va a la API.
// Hook puro de estado (la red la hace productApi); testeable con renderHook. tests/useIaFicha.test.js.
export function useIaFicha({ productId, tipo }) {
  const [generaciones, setGeneraciones] = useState([]); // historial de generaciones (cada una: todos los campos)
  const [idx, setIdx] = useState(0);                     // generación visible
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [revelado, setRevelado] = useState({});          // campo -> true (su tarjeta se muestra)
  const enVueloRef = useRef(false);                      // una sola llamada a la vez

  const total = generaciones.length;
  const sug = generaciones[idx] || null;

  const lanzar = useCallback(async (base) => {
    enVueloRef.current = true; setGenerando(true); setError(null);
    try {
      const d = await sugerirTextosIA(productId, tipo, base);
      setGeneraciones((gs) => { const n = [...gs, d]; setIdx(n.length - 1); return n; });
    } catch (e) { setError((e && e.message) || 'No se pudo generar la sugerencia.'); }
    finally { enVueloRef.current = false; setGenerando(false); }
  }, [productId, tipo]);

  // Revela el campo y, si hace falta, dispara la generación (o espera la que ya está en vuelo).
  const pedir = useCallback((campo, forzar = false, base = 'texto') => {
    setRevelado((r) => (r[campo] ? r : { ...r, [campo]: true }));
    if (!productId) return;
    if (enVueloRef.current) return;      // ya hay una llamada: esperar la misma (ni forzar la duplica)
    if (total && !forzar) return;        // ya hay generación: mostrar la actual, sin llamada
    lanzar(base);
  }, [productId, total, lanzar]);

  const regenerar = useCallback((campo, base = 'texto') => pedir(campo, true, base), [pedir]);
  const irAnterior = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const irSiguiente = useCallback(() => setIdx((i) => Math.min(total - 1, i + 1)), [total]);

  // Usar/Descartar cierran la tarjeta del campo (el historial sigue vivo para los demás) y reportan.
  const resolver = useCallback((campo, desenlace) => {
    if (productId) feedbackIA(productId, campo, desenlace);
    setRevelado((r) => { const n = { ...r }; delete n[campo]; return n; });
  }, [productId]);

  const cargandoDe = (campo) => generando && !!revelado[campo];     // solo los campos pedidos cargan
  const visibleDe = (campo) => !!revelado[campo] && !!sug;          // tarjeta con sugerencia
  const estaRevelado = (campo) => !!revelado[campo];
  return {
    sug, error, generando, total, indice: total ? idx + 1 : 0,
    pedir, regenerar, irAnterior, irSiguiente, resolver,
    cargandoDe, visibleDe, estaRevelado,
  };
}
